import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const PHONE_HREF = 'tel:+905362771709';
const WA_HREF = 'https://wa.me/905362771709';

const slides = [
  {
    img: '/images/home/hero-1.webp',
    label: 'Bioklimatik Pergola',
  },
  {
    img: '/images/home/hero-2.webp',
    label: 'Kasetli Tente',
  },
  {
    img: '/images/home/hero-3.webp',
    label: 'Zip Perde',
  },
];

export default function HeroSection() {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setActive((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section className="relative min-h-screen flex flex-col justify-end overflow-hidden">
      {/* Slides */}
      {slides.map((slide, i) => (
        <div
          key={i}
          className={`absolute inset-0 transition-opacity duration-1000 ${i === active ? 'opacity-100' : 'opacity-0'}`}
        >
          <img
            src={slide.img}
            srcSet={`${slide.img.replace('.webp', '-480.webp')} 480w, ${slide.img} 1200w`}
            sizes="100vw"
            alt={`${slide.label} Alaçatı`}
            width={1200}
            height={896}
            className="w-full h-full object-cover object-top"
            loading={i === 0 ? 'eager' : 'lazy'}
            fetchPriority={i === 0 ? 'high' : 'auto'}
            decoding={i === 0 ? 'sync' : 'async'}
          />
        </div>
      ))}

      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#111827] via-[#111827]/40 to-[#111827]/20"></div>
      <div className="absolute inset-0 bg-gradient-to-r from-[#111827]/60 to-transparent"></div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-8 pb-20 pt-32">
        {/* Slide indicator */}
        <div className="flex items-center gap-2 mb-8">
          {slides.map((slide, i) => (
            <button
              key={i}
              onClick={() => setActive(i)}
              aria-label={slide.label}
              className={`cursor-pointer transition-all duration-300 ${
                i === active ? 'w-8 h-1 bg-[#D4A853]' : 'w-4 h-1 bg-white/30'
              }`}
            />
          ))}
          <span className="ml-3 text-white/50 text-xs tracking-widest uppercase">{slides[active].label}</span>
        </div>

        <div className="max-w-3xl">
          <div className="flex items-center gap-3 mb-4">
            <span className="w-10 h-px bg-[#D4A853]"></span>
            <span className="text-[#D4A853] text-xs font-bold tracking-widest uppercase">
              Alaçatı &amp; İzmir — Ücretsiz Keşif
            </span>
          </div>

          <h1 className="text-5xl md:text-7xl font-black text-white leading-none tracking-tight mb-6">
            Alaçatı<br />
            <span className="text-[#D4A853]">Tenteci</span>
          </h1>
          <p className="text-white/60 text-base md:text-lg leading-relaxed mb-3 max-w-xl">
            Alaçatı ve İzmir Tente, Pergola ve Branda Sistemleri
          </p>
          <p className="text-white/45 text-sm leading-relaxed mb-10 max-w-lg">
            Kasetli tente, bioklimatik pergola, zip perde ve daha fazlası. Ege&apos;nin güneşini ve meltemini kontrol altına alın — uzman ekibimizle ücretsiz keşif hizmetimizden yararlanın.
          </p>

          <div className="flex flex-col sm:flex-row items-start gap-4">
            <a
              href={PHONE_HREF}
              className="flex items-center gap-3 bg-[#D4A853] hover:bg-[#c49840] text-[#111827] font-black text-xs tracking-widest uppercase px-8 py-4 transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line text-base"></i>
              Hemen Ara
            </a>
            <a
              href={WA_HREF}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="flex items-center gap-3 border-2 border-white/40 hover:border-white text-white font-black text-xs tracking-widest uppercase px-8 py-4 transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-whatsapp-line text-base"></i>
              WhatsApp&apos;tan Yaz
            </a>
          </div>
        </div>

        {/* Stats Row */}
        <div className="mt-16 flex flex-wrap gap-8">
          {[
            { value: '500+', label: 'Tamamlanan Proje' },
            { value: '15+', label: 'Yıllık Deneyim' },
            { value: '11', label: 'Farklı Sistem' },
            { value: '6', label: 'Hizmet Bölgesi' },
          ].map((stat) => (
            <div key={stat.label} className="flex items-center gap-3">
              <span className="text-3xl font-black text-[#D4A853]">{stat.value}</span>
              <span className="text-white/40 text-xs leading-tight max-w-[60px]">{stat.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Scroll hint */}
      <div className="absolute bottom-6 right-8 hidden md:flex flex-col items-center gap-2">
        <span className="text-white/30 text-xs tracking-widest uppercase" style={{ writingMode: 'vertical-rl' }}>Keşfet</span>
        <div className="w-px h-12 bg-gradient-to-b from-white/30 to-transparent"></div>
      </div>

      {/* Service quick links */}
      <div className="absolute bottom-0 right-0 hidden lg:flex">
        {['Tente', 'Pergola', 'Branda'].map((cat) => (
          <Link
            key={cat}
            to="/hizmetler"
            className="flex items-center gap-2 bg-[#111827]/80 backdrop-blur-sm border-l border-white/10 text-white/60 hover:text-[#D4A853] text-xs font-semibold tracking-widest uppercase px-6 py-4 cursor-pointer transition-colors whitespace-nowrap"
          >
            {cat}
            <i className="ri-arrow-right-up-line"></i>
          </Link>
        ))}
      </div>
    </section>
  );
}
